import { UrlApi } from "../../../services/apirest";
import {
  CUSTODIA,
  ESTADOS_FISICOS,
  derivarAlertaVencimiento,
  idInstrumentoFisico,
} from "./chequesFisicos.constants";

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : "";
};

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const parseJsonSafe = async (res) => {
  try {
    return await res.json();
  } catch {
    return {};
  }
};

const throwIfError = (res, data, fallback) => {
  if (!res.ok || data?.ok === false) {
    const error = new Error(
      data?.msg || data?.message || data?.error || fallback
    );
    error.status = res.status;
    error.codigo = data?.codigo ?? null;
    throw error;
  }
};

const extraerLista = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const fetchNoStore = (url, extra = {}) =>
  fetch(url, { cache: "no-store", ...extra });

/** id = instrumento del recibo (tipo 3). Estado/custodia default si no hay historial. */
export const normalizarChequeFisico = (cheque) => {
  if (!cheque || typeof cheque !== "object") return cheque;
  const id = idInstrumentoFisico(cheque);
  const normalizado = {
    ...cheque,
    id,
    id_instrumento_bancario: cheque.id_instrumento_bancario ?? id,
    estado: cheque.estado || ESTADOS_FISICOS.DISPONIBLE,
    estado_custodia:
      cheque.estado_custodia || cheque.custodia || CUSTODIA.EN_SUCURSAL,
    dias_desde_emision:
      cheque.dias_desde_emision != null && cheque.dias_desde_emision !== ""
        ? Number(cheque.dias_desde_emision)
        : cheque.dias_desde_emision,
    dias_para_vencimiento:
      cheque.dias_para_vencimiento != null && cheque.dias_para_vencimiento !== ""
        ? Number(cheque.dias_para_vencimiento)
        : cheque.dias_para_vencimiento,
  };
  return {
    ...normalizado,
    alerta_vencimiento: derivarAlertaVencimiento(normalizado),
  };
};

export const extraerChequesFisicos = (data) =>
  extraerLista(data, ["cheques", "items", "data"]).map(normalizarChequeFisico);

export const normalizarSucursalFisica = (sucursal) => {
  if (!sucursal || typeof sucursal !== "object") return sucursal;
  const tieneRecaudadora = Boolean(
    sucursal.tiene_recaudadora ?? sucursal.cheques_tiene_recaudadora
  );
  const tiposRaw = sucursal.tipos_deposito_permitidos;
  const tiposDeposito = Array.isArray(tiposRaw)
    ? tiposRaw.map((t) => String(t).toLowerCase())
    : tieneRecaudadora
      ? ["banco", "recaudadora"]
      : ["banco"];
  return {
    ...sucursal,
    tiene_recaudadora: tieneRecaudadora,
    tipos_deposito_permitidos: tiposDeposito,
    puede_pagar_con_fisicos: Boolean(sucursal.puede_pagar_con_fisicos),
    cheques_alerta_proximo: Number(sucursal.cheques_alerta_proximo ?? 0),
    cheques_alerta_vencido: Number(sucursal.cheques_alerta_vencido ?? 0),
  };
};

export const extraerSucursalesFisicos = (data) =>
  extraerLista(data, ["sucursales", "items", "data"]).map(
    normalizarSucursalFisica
  );

export const extraerHistorialFisico = (data, cheque) => {
  const lista = extraerLista(data, [
    "historial",
    "cheques_fisicos_historial",
    "movimientos",
  ]);
  const fromCheque = extraerLista(cheque, [
    "historial",
    "cheques_fisicos_historial",
    "movimientos",
  ]);
  const rows = lista.length ? lista : fromCheque;
  return [...rows].sort((a, b) => {
    const ta = new Date(a?.fecha || a?.created_at || 0).getTime();
    const tb = new Date(b?.fecha || b?.created_at || 0).getTime();
    return tb - ta;
  });
};

export const getChequesFisicosResumen = async () => {
  const res = await fetchNoStore(`${UrlApi}/cheques-fisicos/resumen`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo obtener el resumen de cheques físicos");
  return data;
};

export const getSucursalesChequesFisicos = async () => {
  const res = await fetchNoStore(`${UrlApi}/cheques-fisicos/sucursales`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudieron obtener las sucursales");
  return extraerSucursalesFisicos(data);
};

export const getChequesFisicos = async (filtros = {}) => {
  const {
    id_sucursal,
    estado,
    estado_custodia,
    alerta_vencimiento,
    alerta_30,
    q,
    fecha_pago_desde,
    fecha_pago_hasta,
    origen_cheque,
    limit = 50,
    offset = 0,
  } = filtros;

  const query = buildQuery({
    id_sucursal,
    estado,
    estado_custodia,
    alerta_vencimiento,
    alerta_30,
    q,
    fecha_pago_desde,
    fecha_pago_hasta,
    origen_cheque,
    limit,
    offset,
  });

  const res = await fetchNoStore(`${UrlApi}/cheques-fisicos${query}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudieron obtener los cheques físicos");
  return {
    ...data,
    cheques: extraerChequesFisicos(data),
  };
};

export const getChequeFisicoById = async (id, { origen_cheque } = {}) => {
  const idInst = idInstrumentoFisico({ id }) ?? id;
  const query = buildQuery({ origen_cheque });
  const res = await fetchNoStore(`${UrlApi}/cheques-fisicos/${idInst}${query}`, {
    headers: authHeaders(),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo obtener el cheque físico");
  const chequeRaw =
    data?.cheque && typeof data.cheque === "object" ? data.cheque : data;
  const cheque = normalizarChequeFisico(chequeRaw);
  return {
    ...data,
    cheque,
    historial: extraerHistorialFisico(data, chequeRaw),
  };
};

export const depositarChequeFisico = async (
  id,
  { tipo_deposito, banco, recaudadora, obs, foto } = {}
) => {
  const user = localStorage.getItem("user") || "";
  const payload = {
    tipo_deposito: tipo_deposito || undefined,
    banco: banco || undefined,
    recaudadora: recaudadora || undefined,
    obs: obs || undefined,
    user: user || undefined,
  };

  let res;
  if (foto) {
    const formData = new FormData();
    formData.append("foto", foto);
    Object.entries(payload).forEach(([key, value]) => {
      if (value != null && value !== "") formData.append(key, value);
    });
    res = await fetch(`${UrlApi}/cheques-fisicos/${id}/depositar`, {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    });
  } else {
    res = await fetch(`${UrlApi}/cheques-fisicos/${id}/depositar`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
  }
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo depositar el cheque");
  return data;
};

export const enviarChequeFisicoCc = async (id, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques-fisicos/${id}/enviar-cc`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      obs: body.obs || undefined,
      user: localStorage.getItem("user") || undefined,
    }),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo enviar el cheque a Casa Central");
  return data;
};

export const enviarChequesFisicosCcLote = async (ids = [], body = {}) => {
  const res = await fetch(`${UrlApi}/cheques-fisicos/enviar-cc`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      ids,
      obs: body.obs || undefined,
      user: localStorage.getItem("user") || undefined,
    }),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudieron enviar los cheques a Casa Central");
  return data;
};

export const recibirChequeFisicoCc = async (id, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques-fisicos/${id}/recibir-cc`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      obs: body.obs || undefined,
      user: localStorage.getItem("user") || undefined,
    }),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo marcar el cheque como recibido en Casa Central");
  return data;
};

export const marcarFaltanteChequeFisico = async (id, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques-fisicos/${id}/faltante`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      obs: body.obs || undefined,
      observacion: body.obs || undefined,
      user: localStorage.getItem("user") || undefined,
    }),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo registrar el faltante");
  return data;
};

export const corregirChequeFisico = async (id, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques-fisicos/${id}/corregir`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      nro_cheque: body.nro_cheque || undefined,
      fecha_pago: body.fecha_pago || undefined,
      fecha_emision: body.fecha_emision || undefined,
      obs: body.obs || undefined,
      user: localStorage.getItem("user") || undefined,
    }),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo corregir el cheque");
  return data;
};

/** Mismo circuito de ND que eCheq, sobre /cheques-fisicos. */
export const postRechazoChequeFisico = async (idCheque, body = {}) => {
  const res = await fetch(`${UrlApi}/cheques-fisicos/${idCheque}/rechazo`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseJsonSafe(res);
  throwIfError(res, data, "No se pudo registrar el rechazo");
  return data;
};
